import { reactive } from 'vue';

const appConfig = {
  // 全局配置 - version data comes from backend API only
  // Use reactive for Vue 3 reactivity
  config: reactive({
    version: null,
    releaseDate: null,
    files: null,
    // Loading state
    loading: true,
    error: null,
  }),

  /**
   * Set version config from backend API response
   * @param {Object} versionData - { version, release_date, files }
   */
  setVersionConfig(versionData) {
    this.config.loading = false;
    this.config.error = null;
    this.config.version = versionData.version;
    this.config.releaseDate = versionData.release_date;
    this.config.files = versionData.files || [];
  },

  /**
   * Set error state when API fails
   * @param {string} errorMessage
   */
  setVersionError(errorMessage) {
    this.config.loading = false;
    this.config.error = errorMessage || '版本信息获取失败';
  },

  // 计算属性 - 用于动态生成URL和描述
  computed: {
    get isReady() {
      return !appConfig.config.loading && !appConfig.config.error && appConfig.config.version;
    },
    get versionDisplay() {
      if (!appConfig.config.version) {
        return '';
      }
      return `${appConfig.config.version} ${appConfig.config.releaseDate || ''}`;
    },
    get downloadUrls() {
      // Version data not loaded yet
      if (!appConfig.config.files || appConfig.config.files.length === 0) {
        return null;
      }
      
      const filesMap = {};
      appConfig.config.files.forEach(file => {
        filesMap[file.key] = file.url;
      });
      
      // Map browser keys to file keys
      const commonUrl = filesMap.common || filesMap.default;
      const chromeUrl = filesMap.chrome || commonUrl;
      
      return {
        qq: commonUrl,
        "360ee": commonUrl,
        "360x": commonUrl,
        chrome: chromeUrl,
        sogou: commonUrl,
        quark: commonUrl,
      };
    },
  },

  header: {
    brand: "咖喱阅卷",
    home: "首页",
    install_guide: "安装教程",
    usage_guide: "使用教程",
    update_guide: "更新教程",
    contact_us: "联系我们",
    download_app: "下载咖喱阅卷",
    logo_image: "/images/logo.png",
    // 添加链接配置
    links: {
      install_guide_url:
        "https://maplebolo.feishu.cn/wiki/KR0Aw2sNci5YUikZFGpcS1SMnig",
      usage_guide_url:
        "https://maplebolo.feishu.cn/wiki/DMyTwGr6JiYykWkzWsTcj6EknTb",
      update_guide_url:
        "https://maplebolo.feishu.cn/docx/TdMmdwE2roEQ2sxfPC0cBFxdnoh",
      contact_us_url: "#",
    },
  },
  footer: {
    brand: "咖喱阅卷",
    contact_us: "联系我们",
    logo_image: "/images/logo.png",
    links: {
      qq_group: {
        text: "官方Q群(可领阅卷次数)",
        logo: "/images/logo_qq.png",
        url: "https://qm.qq.com/q/UMvPONHPqI", // 添加QQ群链接
      },
      xiaohongshu: {
        text: "小红书",
        logo: "/images/logo_xhs.png",
        url: "https://www.xiaohongshu.com/user/profile/5fe49b5b0000000001002806", // 添加小红书链接
      },
      bilibili: {
        text: "Bilibili",
        logo: "/images/logo_bilibili.png",
        url: "https://space.bilibili.com/73900277", // 添加B站链接
      },
      wechat_channel: {
        text: "抖音",
        logo: "/images/logo_douyin.png",
        url: "https://www.douyin.com/user/MS4wLjABAAAAlLNAYpuxkBG8RjpO1nN8N0gKdwUYdgxZxQMy4-Re-3tr_QK1FORfJkH88ONRSDP9",
      },
    },
    quick_links: "快捷跳转",
    quick_links_items: {
      download: "下载页",
      install_guide: "安装教程",
      usage_guide: "使用教程",
      faq: "常见问题FAQ",
      update: "更新教程",
    },
    // 添加快捷链接的URL配置
    quick_links_urls: {
      download: "/download", // 跳转到下载页
      install_guide:
        "https://maplebolo.feishu.cn/wiki/KR0Aw2sNci5YUikZFGpcS1SMnig", // 安装教程飞书链接
      usage_guide:
        "https://maplebolo.feishu.cn/wiki/DMyTwGr6JiYykWkzWsTcj6EknTb", // 使用教程飞书链接
      faq: "https://maplebolo.feishu.cn/wiki/XI1BwxOJriGskdku51ocyU71n7b", // FAQ飞书链接
      update: "https://maplebolo.feishu.cn/docx/TdMmdwE2roEQ2sxfPC0cBFxdnoh", // 更新教程飞书链接
    },
  },
  hero: {
    title: "国内最好用的<br />AI自动阅卷软件",
    subtitle: "让老师摆脱繁重的阅卷任务，轻松教学，告别手工劳动",
    download_button: "前往下载",
    background_image:
      "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/KV_100.jpg",
  },
  features: {
    install_fast: {
      title: "安装快捷",
      description: "步骤简单<br />10秒即可完成安装",
      icon: "/images/feature_1.svg",
    },
    smart_accurate: {
      title: "智能准确",
      description: "字迹潦草不要慌<br />精准识别, 智能阅卷",
      icon: "/images/feature_2.svg",
    },
    full_coverage: {
      title: "全面覆盖",
      description: "全学科覆盖 (开发中)<br />全阅卷平台适配 (开发中)",
      icon: "/images/feature_3.svg",
    },
    free_to_use: {
      title: "免费使用",
      description: "当前测试版本<br />下载即赠免费次数",
      icon: "/images/feature_4.svg",
    },
  },
  home: {
    reasons_to_use: "使用<strong>咖喱阅卷</strong>的理由",
    section1: {
      title: "10秒完成安装",
      description:
        "-告别传统繁琐安装\n-一键安装无需多余操作\n-适配Window Mac全系统",
      image:
        "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/feature%E4%BB%8B%E7%BB%8D-1.png",
    },
    section2: {
      title: "识别精准 阅卷精准",
      description:
        "-再潦草的字迹也能准确识别\n-咖喱阅卷的评分与学校校对误差小于5%，优于手动阅卷\n-配置后完全托管，自动完成所有阅卷",
      image:
        "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/feature%E4%BB%8B%E7%BB%8D-2.png",
    },
    section3: {
      title: "全平台 全学科逐步适配中",
      description:
        "-目前适配语文全题型\n-目前适配平台：智学网，五岳阅卷、七天阅卷、好分数\n-正在适配所有题型和平台",
      image:
        "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/feature%E4%BB%8B%E7%BB%8D-3.png",
    },
    section4: {
      title: "测试阶段 阅卷次数免费送",
      description:
        "-下载即赠阅卷次数，兑换码galiyuejuan再兑次数\n-进入官方QQ群持续获赠阅卷次数",
      image:
        "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/feature%E4%BB%8B%E7%BB%8D-4.png",
    },
  },
  testimonials: {
    title: "用户评价",
    reviews: [
      {
        title: "安装简单易用",
        text: "插件安装非常便捷，上手快，操作直观，零基础也能轻松使用，极大提高了工作效率。",
        author: "语文谭老师",
        avatar: "/images/user_comments_1.png",
      },
      {
        title: "阅卷智能精准",
        text: "批改分数非常准确，学生的答案识别特别到位，大大提高了批改的公平性和效率。",
        author: "数学赵老师",
        avatar: "/images/user_comments_2.png",
      },
      {
        title: "减负高效助教",
        text: "自动阅卷大幅减轻了我的负担，让我有更多时间专注教学和生活，真的很实用！",
        author: "历史王老师",
        avatar: "/images/user_comments_3.png",
      },
    ],
  },
  download_page: {
    title: "安装咖喱阅卷",
    browsers: {
      edge: {
        name: "Edge浏览器",
        button_text: "前往安装",
        download_url:
          "https://microsoftedge.microsoft.com/addons/detail/%E5%92%96%E5%96%B1%E9%98%85%E5%8D%B7/hhflnhodhiakgaiahhcnkklbcjiokiap",
        get description() {
          return `最新版本：${appConfig.computed.versionDisplay}`;
        },
        steps: [
          {
            title: "1、打开Edge插件商店",
            description:
              "点击【前往安装】按钮，网站会跳转至【咖喱阅卷】的安装页面",
            image:
              "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/guide/edge1.gif",
          },
          {
            title: "2、完成安装",
            description:
              "点击【获取】按钮，在弹出窗口点击【添加拓展】，等待2秒，完成咖喱阅卷安装（弹出提醒关闭即可）",
            image:
              "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/guide/edge2.gif",
          },
          {
            title: "3、准备阅卷",
            description: "先前往阅卷任务，在点击【咖喱阅卷】按钮，开始阅卷",
            image:
              "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/guide/edge3.gif",
          },
        ],
      },
      qq: {
        name: "QQ浏览器",
        button_text: "开始下载",
        get download_url() {
          return appConfig.computed.downloadUrls.qq;
        },
        get description() {
          return `最新版本：${appConfig.computed.versionDisplay}`;
        },
        steps: [
          {
            title: "1、下载插件文件",
            description:
              "点击【开始下载】，完成下载后，找到下载的压缩文件，解压它",
            image:
              "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/guide/qq-1-buildcrx.gif",
          },
          {
            title: "2、打开应用中心",
            description: "点击浏览器右上角【≡】按钮，找到应用中心并点击",
            image:
              "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/guide/QQ2.gif",
          },
          {
            title: "3、安装插件",
            description:
              "首先，在应用中心，勾选右上角的【开发者模式】；然后鼠标左键选中刚解压的文件，长按拖拽到浏览器中，完成安装",
            image:
              "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/guide/qq-crx-3.gif",
          },
        ],
      },
      "360ee": {
        name: "360安全浏览器",
        button_text: "开始下载",
        get download_url() {
          return appConfig.computed.downloadUrls["360ee"];
        },
        get description() {
          return `最新版本：${appConfig.computed.versionDisplay}`;
        },
        steps: [
          {
            title: "1、下载插件文件",
            description:
              "选择360安全浏览器，点击【开始下载】，找到下载的压缩文件，解压它",
            image:
              "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/guide/360safe-1-buildcrx.gif",
          },
          {
            title: "2、进入拓展管理",
            description: "在网址输入se://extensions 进入拓展管理页",
            image:
              "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/guide/360%E5%AE%89%E5%85%A82.gif",
          },
          {
            title: "3、安装插件",
            description:
              "首先，在拓展管理页，勾选右上角的【开发者模式】；然后鼠标左键选中刚解压的文件，长按拖拽到浏览器中，完成安装",
            image:
              "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/guide/360safe-crx-3.gif",
          },
        ],
      },
      "360x": {
        name: "360极速浏览器",
        button_text: "无法安装",
        get download_url() {
          return null;
        },
        get description() {
          return "当前浏览器暂不支持，请在网站底部找到安装教程2步（20秒）完成安装，或加群联系我们";
        },
        steps: [],
      },
      chrome: {
        name: "Chrome浏览器",
        button_text: "开始下载",
        get download_url() {
          return appConfig.computed.downloadUrls.chrome;
        },
        get description() {
          const chromeStoreUrl =
            "https://chromewebstore.google.com/detail/%E5%92%96%E5%96%B1%E9%98%85%E5%8D%B7/aceegobmnelgodiomkfmeknnladhbefg?hl=zh-CN&utm_source=ext_sidebar";
          return `最新版本：${appConfig.computed.versionDisplay} <br> 如果你的电脑可以正常访问谷歌，可前往应用商店一键安装👉 <a href='${chromeStoreUrl}' style='color: #0D3A4A; text-decoration: underline;'>前往应用商店</a>`;
        },
        steps: [
          {
            title: "1、下载插件文件",
            description:
              "选择Chrome浏览器，点击【开始下载】，找到下载的压缩文件，解压它",
            image:
              "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/guide/chrome-1-buildcrx.gif",
          },
          {
            title: "2、进入拓展管理",
            description: "在网址输入chrome://extensions/ 进入拓展管理页",
            image:
              "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/guide/chrome2.gif",
          },
          {
            title: "3、安装插件",
            description:
              "首先，在拓展管理页，勾选右上角的【开发者模式】；然后鼠标左键选中刚解压的文件，长按拖拽到浏览器中，完成安装",
            image:
              "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/guide/chrome-crx-3.gif",
          },
        ],
      },
      sogou: {
        name: "搜狗浏览器",
        button_text: "开始下载",
        get download_url() {
          return appConfig.computed.downloadUrls.sogou;
        },
        get description() {
          return `最新版本：${appConfig.computed.versionDisplay}`;
        },
        steps: [
          {
            title: "1、下载插件文件",
            description:
              "点击【开始下载】，完成下载后，找到下载的压缩文件，解压它",
            image:
              "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/guide/sougou-1-buildcrx.gif",
          },
          {
            title: "2、进入拓展管理",
            description:
              "在浏览器上方点击【工具】按钮，点击【我的工具】，进入应用中心",
            image:
              "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/guide/%E6%90%9C%E7%8B%972.gif",
          },
          {
            title: "3、安装插件",
            description:
              "首先，在应用中心，勾选右上角的【开发者模式】；然后鼠标左键选中刚解压的文件，长按拖拽到浏览器中，完成安装",
            image:
              "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/guide/sogou-crx-3.gif",
          },
        ],
      },
      quark: {
        name: "夸克浏览器",
        button_text: "开始下载",
        get download_url() {
          return appConfig.computed.downloadUrls.quark;
        },
        get description() {
          return `最新版本：${appConfig.computed.versionDisplay}`;
        },
        steps: [
          {
            title: "1、下载插件文件",
            description:
              "点击【开始下载】，完成下载后，找到下载的压缩文件，解压它",
            image:
              "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/guide/quark-1-buildcrx.gif",
          },
          {
            title: "2、进入拓展管理",
            description:
              "在浏览器上方点击【插件】按钮，点击【插件管理】，进入插件中心",
            image:
              "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/guide/%E5%A4%B8%E5%85%8B2.gif",
          },
          {
            title: "3、安装插件",
            description:
              "首先，在插件中心，勾选左下角的【开发者模式】；然后鼠标左键选中刚解压的文件，长按拖拽到浏览器中，完成安装",
            image:
              "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/guide/kuake-crx-3.gif",
          },
        ],
      },
      safari: {
        name: "Safari浏览器",
        button_text: "下载 Edge 浏览器",
        download_url: "https://www.microsoft.com/zh-cn/edge",
        description: "苹果电脑需要先安装Edge浏览器，并用Edge浏览器打开本网站",
        steps: [
          {
            title: "1、安装Edge浏览器",
            description: "前往Edge浏览器官网 <a href='https://www.microsoft.com/zh-cn/edge' target='_blank' style='color: #0D3A4A; text-decoration: underline;'>https://www.microsoft.com/zh-cn/edge</a>，安装Edge浏览器",
            image: "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/guide/safari1.gif"
          },
          {
            title: "2、使用Edge浏览器打开本网站",
            description: "打开Edge浏览器，输入咖喱阅卷官网 galiyuejuan.com",
            image: "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/guide/safari2.gif"
          },
          {
            title: "3、完成安装",
            description: "在下载页，点击“前往安装”，完成插件安装",
            image: "https://galiguluweb-1369486987.cos.ap-guangzhou.myqcloud.com/guide/safari3.gif"
          }
        ]
      },
      other: {
        name: 'other',
        button_text: "无法安装",
        download_url: null,
        description:
          "当前浏览器暂不支持，请在网站底部找到安装教程2步（20秒）完成安装，或加群联系我们",
        steps: [],
      },
    },
    footer: {
      supported_browsers:
        "如果您无法顺利安装，请在网站底部找到联系方式联系我们，或查看安装教程学习简易安装方式<br>目前支持的浏览器：Edge、QQ、搜狗、360安全、Chrome、夸克浏览器",
    },
  },
  content_section: {
    download_button: "前往下载",
  },
};

export default appConfig;
